/**
 * Shared helpers for the Profit / Loss report view, PDF, and CSV.
 * @module profitLossFormatters
 */

import { format } from "date-fns";
import {
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatPerMile,
} from "../InvoiceAnalyticsPanel/analyticsFormatters";

export { formatMoney, formatMonth, formatNumber, formatPercent, formatPerMile };

/**
 * "Jan 1, 2026 - Mar 31, 2026", or "All time" when the report has no period.
 * ASCII only so jsPDF's built-in fonts can draw it.
 * @param {{ startDate?: string, endDate?: string }|null} period
 * @returns {string}
 */
export const formatPeriod = (period) => {
  if (!period || (!period.startDate && !period.endDate)) return "All time";
  const fmt = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime())
      ? format(date, "MMM d, yyyy")
      : "Open";
  };
  return `${fmt(period.startDate)} - ${fmt(period.endDate)}`;
};

/**
 * Headline figures shown as cards and in the PDF overview.
 * @param {object} summary - `report.summary`
 * @returns {Array<{ key: string, label: string, value: string, tone?: "positive"|"negative" }>}
 */
export const buildProfitLossKpis = (summary = {}) => {
  const tone = (value) => (value < 0 ? "negative" : value > 0 ? "positive" : undefined);
  return [
    { key: "revenue", label: "Revenue", value: formatMoney(summary.revenue) },
    { key: "totalCosts", label: "Total costs", value: formatMoney(summary.totalCosts) },
    {
      key: "grossProfit",
      label: "Gross profit",
      value: formatMoney(summary.grossProfit),
      tone: tone(summary.grossProfit),
    },
    {
      key: "netProfit",
      label: "Net profit",
      value: formatMoney(summary.netProfit),
      tone: tone(summary.netProfit),
    },
    {
      key: "netMargin",
      label: "Net margin",
      value: formatPercent(summary.netMargin),
      tone: tone(summary.netMargin),
    },
    {
      key: "profitPerMile",
      label: "Profit / mile",
      value: formatPerMile(summary.profitPerMile),
      tone: tone(summary.profitPerMile),
    },
  ];
};

/**
 * Secondary metrics grouped for the detail cards and PDF.
 * @param {object} summary - `report.summary`
 * @returns {Array<{ id: string, title: string, rows: Array<{ label: string, value: string }> }>}
 */
export const buildProfitLossMetricSections = (summary = {}) => [
  {
    id: "revenue",
    title: "Revenue",
    rows: [
      { label: "Invoices", value: formatNumber(summary.invoiceCount) },
      { label: "Collected", value: formatMoney(summary.collected) },
      { label: "Outstanding", value: formatMoney(summary.outstanding) },
      { label: "Overdue", value: formatMoney(summary.overdue) },
      { label: "Fuel surcharge", value: formatMoney(summary.fuelRevenue) },
      { label: "Fuel % of revenue", value: formatPercent(summary.fuelPercentOfRevenue) },
    ],
  },
  {
    id: "costs",
    title: "Costs",
    rows: [
      { label: "Payouts", value: formatNumber(summary.payoutCount) },
      { label: "Labor", value: formatMoney(summary.laborCost) },
      { label: "Labor % of revenue", value: formatPercent(summary.laborPercentOfRevenue) },
      { label: "Expenses", value: formatMoney(summary.expenseCost) },
      { label: "Expense % of revenue", value: formatPercent(summary.expensePercentOfRevenue) },
      { label: "Gross margin", value: formatPercent(summary.grossMargin) },
    ],
  },
  {
    id: "unit",
    title: "Per unit",
    rows: [
      { label: "Billed miles", value: formatNumber(summary.billedMiles) },
      { label: "Paid miles", value: formatNumber(summary.paidMiles) },
      { label: "Revenue / mile", value: formatPerMile(summary.revenuePerMile) },
      { label: "Labor cost / mile", value: formatPerMile(summary.laborCostPerMile) },
      { label: "Total cost / mile", value: formatPerMile(summary.costPerMile) },
      { label: "Profit / job", value: formatMoney(summary.profitPerJob) },
      { label: "Profit / leg", value: formatMoney(summary.profitPerLeg) },
    ],
  },
];

/**
 * Flat rows for CSV export: statement lines, then the monthly breakdown.
 * @param {object} report - Profit / Loss response
 * @returns {Array<object>}
 */
export const buildProfitLossCsvRows = (report) => {
  const statement = (report?.data || []).map((row) => ({
    Section: row.section,
    Line: row.label,
    Amount: row.amount,
    "% of revenue": row.percentOfRevenue,
    Month: "",
    Revenue: "",
    "Labor cost": "",
    "Expense cost": "",
    "Net profit": "",
    "Net margin %": "",
  }));
  const months = (report?.byMonth || []).map((row) => ({
    Section: "Monthly",
    Line: "",
    Amount: "",
    "% of revenue": "",
    Month: row.month,
    Revenue: row.revenue,
    "Labor cost": row.laborCost,
    "Expense cost": row.expenseCost,
    "Net profit": row.netProfit,
    "Net margin %": row.netMargin,
  }));
  return [...statement, ...months];
};
